import type { SectionKey } from './api/navigation.ts';
import type { RecordOpen } from './tasks/model.ts';

/**
 * A client's Timeline (A-#157, Arthur, 27 October 2026), pure so it is tested without a browser: everything that has
 * happened on the client, newest first. The server decides what the reader sees (each source read as the reader, a
 * kind gated on its section); this says how an entry reads and where it opens.
 */

export type ActivityKind = 'task' | 'action' | 'communication' | 'visit' | 'site_visit' | 'filing' | 'court_order' | 'death'
  | 'commission' | 'oath_designation' | 'bond' | 'marshalling' | 'commission_blocker';

export interface ActivityEntry {
  kind: ActivityKind;
  id: string;
  /** when it happened, an instant; null for a kind recorded by its day */
  at: string | null;
  /** the day it happened, a calendar day (never converted through a zone); null where `at` is given, and for a death
   *  whose date is not known (both null: "Date not known") */
  on: string | null;
  department: string | null;
  what: string | null;
  by: string | null;
  minutes: number | null;
  note: string | null;
  /** an earlier version is kept (the item's own page shows them) */
  edited: boolean;
  /** the id the item's link needs: the task, the entry, the property (a site visit), the deadline (a filing) */
  ref: string;
  /** deploy 8: the agency's month it is counted in (YYYY-MM), the server's, so an undated death is placed too */
  month: string;
}

export interface ActivityPage {
  /** the kinds this reader may see on this client, in the server's order */
  kinds: ActivityKind[];
  entries: ActivityEntry[];
  next: { before: string; before_id: string } | null;
}

export interface ActivityFilter { kind?: ActivityKind; department?: string }

/**
 * Deploy 8 (A-#227, closing P-13): the ONE map from a kind of entry or record to the section that shows it. The
 * Timeline's links and cards, the closed task's facts and its link all ask this; null: the item opens in place (a task,
 * an action) and row security alone decides.
 */
export const KIND_SECTION: Record<ActivityKind, SectionKey | null> = {
  task: null, action: null, communication: 'communications', visit: 'sw', site_visit: 'property', filing: 'reports',
  court_order: 'legal', death: 'legal', commission: 'legal', oath_designation: 'legal', bond: 'legal',
  // 166 (P-33): a marshalling step, in the Finance section
  marshalling: 'financials',
  // 167 (P-24): a cleared commission blocker, in the Legal section
  commission_blocker: 'legal',
};
/** Whether the reader holds the section that shows this kind (true for a kind that opens in place). */
export function holdsKind(kind: ActivityKind, sections: readonly SectionKey[]): boolean {
  const s = KIND_SECTION[kind];
  return s === null || sections.includes(s);
}

/** Deploy 8: a month's count, YYYY-MM (the agency's calendar). */
export interface ActivityMonth { month: string; n: number }
export interface ActivityMonths { kinds: ActivityKind[]; months: ActivityMonth[] }

export const KIND_WORDS: Record<ActivityKind, string> = {
  task: 'Task done', action: 'Action', communication: 'Communication', visit: 'Visit', site_visit: 'Site visit',
  filing: 'Report filed', court_order: 'Court order', death: 'Death recorded',
  commission: 'Commission', oath_designation: 'Oath and designation', bond: 'Bond', marshalling: 'Marshalling',
  commission_blocker: 'Commission blocker cleared',
};

/** The departments the filter offers (the communications log's seven, which hold the five task departments). */
export const ACTIVITY_DEPARTMENTS = ['Social Work', 'Finance', 'Legal', 'Property', 'Compliance', 'Executive', 'AP/AR'] as const;

/** The page's address: fixed-shape keys only (a kind, a department, an instant and a uuid), never free text. */
export function activityPath(clientId: string, f: ActivityFilter, cursor: { before: string; before_id: string } | null,
  month?: string, since?: string): string {
  const q = new URLSearchParams({ limit: '50' });
  if (f.kind) q.set('kind', f.kind);
  if (f.department) q.set('department', f.department);
  if (month) q.set('month', month);
  else if (since) q.set('since', since);
  if (cursor) { q.set('before', cursor.before); q.set('before_id', cursor.before_id); }
  return `/clients/${clientId}/activity?${q.toString()}`;
}

/** Deploy 8: the months' counts, with the same filters (fixed-shape keys only). */
export function activityMonthsPath(clientId: string, f: ActivityFilter): string {
  const q = new URLSearchParams();
  if (f.kind) q.set('kind', f.kind);
  if (f.department) q.set('department', f.department);
  const qs = q.toString();
  return `/clients/${clientId}/activity/months${qs ? `?${qs}` : ''}`;
}

/** The agency's current month, YYYY-MM, from an instant (New York, never the viewer's zone). */
export function agencyMonth(now: Date): string {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit' }).formatToParts(now);
  return `${parts.find((p) => p.type === 'year')!.value}-${parts.find((p) => p.type === 'month')!.value}`;
}

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October',
  'November', 'December'];
/** "September", from YYYY-MM. */
export const monthName = (month: string) => MONTH_NAMES[Number(month.slice(5, 7)) - 1]!;
/** "8 entries", "1 entry". */
export const entriesSaid = (n: number) => `${n} ${n === 1 ? 'entry' : 'entries'}`;

/**
 * A-#238: the months a new plan opens that the last one did not (a month that appeared while the Timeline was open,
 * the counts read again): opened too, never left collapsed until a filter changes.
 */
export function newlyOpen(before: readonly string[], after: readonly string[]): string[] {
  return after.filter((m) => !before.includes(m));
}

export interface TimelineYear { year: string; n: number; months: ActivityMonth[]; open: boolean }
export interface TimelinePlan { years: TimelineYear[]; open: string[] }

/**
 * Deploy 8 (A-#227): what the Timeline shows open at first. Newest first: whole months are opened until at least `min`
 * entries show, and the current month is always open (shown even with none). Every other month is collapsed to its
 * count; a year is shown open when it is the current year or holds an open month, else collapsed to its total.
 */
export function timelinePlan(months: readonly ActivityMonth[], current: string, min = 12): TimelinePlan {
  const all = [...months];
  if (!all.some((m) => m.month === current)) all.push({ month: current, n: 0 });
  all.sort((a, b) => (a.month < b.month ? 1 : a.month > b.month ? -1 : 0));
  const open: string[] = [];
  let shown = 0;
  for (const m of all) {
    if (m.month > current) continue;
    if (m.month === current || shown < min) { open.push(m.month); shown += m.n; }
  }
  // a month after the current one (a dated entry in the future) is shown open too, above it
  for (const m of all) if (m.month > current) open.unshift(m.month);
  const years: TimelineYear[] = [];
  for (const m of all) {
    const y = m.month.slice(0, 4);
    let year = years[years.length - 1];
    if (!year || year.year !== y) { year = { year: y, n: 0, months: [], open: false }; years.push(year); }
    year.months.push(m); year.n += m.n;
    if (open.includes(m.month) || y === current.slice(0, 4)) year.open = true;
  }
  return { years, open };
}

/**
 * Where an entry opens: a task or an action opens its card in place ('card'); the rest open their section of the
 * client's record, a site visit at its property's card. Words alone (null) where the reader holds no section that
 * would show it.
 */
export function entryLink(clientId: string, e: ActivityEntry, sections: readonly SectionKey[]): string | 'card' | 'visit' | null {
  if (!holdsKind(e.kind, sections)) return null;
  const at = (path: string) => `/clients/${clientId}/${path}`;
  switch (e.kind) {
    case 'task': case 'action': return 'card';
    case 'communication': return at('communications');
    case 'visit': return 'visit';
    case 'site_visit': return at(`property/${e.ref}#site-visit-${e.id}`);
    case 'filing': return at('reports');
    case 'court_order': case 'death': return at('legal');
    case 'commission': case 'oath_designation': case 'bond': case 'commission_blocker': return at('legal');
    case 'marshalling': return at('finance');
  }
}

/** "7 min", "1 h 30 min": the time spent, or null where none was recorded. */
export function minutesSaid(m: number | null): string | null {
  if (m === null) return null;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return h === 0 ? `${r} min` : r === 0 ? `${h} h` : `${h} h ${r} min`;
}

/**
 * A-#194: what the entry's own screen is asked to open, so it opens the record itself: a filing at its row on Reports
 * (a site visit is its property's page at #site-visit-<id>, A-#195 1); a commission or an oath at its order in Legal
 * (A-#211). Undefined for the rest.
 */
export function entryState(e: ActivityEntry): RecordOpen | undefined {
  // deploy 8: the record's place says where it was opened from (OpenedRecord's label)
  if (e.kind === 'filing') return { show_filing: e.ref, opened_from: 'timeline' };
  if (e.kind === 'commission' || e.kind === 'oath_designation') return { show_order: e.ref, opened_from: 'timeline' };
  // 162 (P-35): a bond, at its order in Legal (ref is the order; a bond not yet tied to one opens Legal plain)
  if (e.kind === 'bond') return e.ref ? { show_order: e.ref, opened_from: 'timeline' } : undefined;
  // 167 (P-24): a commission blocker cleared, at its order in Legal (none where no order was acting)
  if (e.kind === 'commission_blocker') return e.ref ? { show_order: e.ref, opened_from: 'timeline' } : undefined;
  if (e.kind === 'site_visit') return { opened_from: 'timeline' };
  return undefined;
}
